/**
 * Campaigns List Page - Shows all public campaigns
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, ArrowRight, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignsList = () => {
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['public-campaigns'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns`);
      return res.json();
    }
  });
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-emerald-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Support Our Causes</h1>
          <p className="text-emerald-100 text-lg max-w-2xl mx-auto">
            Join us in making a difference. Every donation brings hope to communities in need.
          </p>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Heart className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h2 className="text-xl font-semibold text-slate-600 mb-2">No Active Campaigns</h2>
              <p className="text-slate-500">Check back soon for new fundraising opportunities.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => {
              const progress = campaign.target_amount > 0 
                ? Math.round((campaign.raised_amount / campaign.target_amount) * 100) 
                : 0;

              return (
                <Card key={campaign.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                  {/* Campaign Image */}
                  <div className="h-48 relative overflow-hidden">
                    {campaign.hero_image ? (
                      <img 
                        src={campaign.hero_image}
                        alt={campaign.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center">
                        <Heart className="h-16 w-16 text-white/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg line-clamp-2">{campaign.name}</h3>
                    </div>
                  </div>

                  <CardContent className="p-4">
                    {campaign.tagline && (
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">{campaign.tagline}</p>
                    )}

                    {/* Progress */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-semibold text-emerald-600">
                          ${(campaign.raised_amount || 0).toLocaleString()}
                        </span>
                        <span className="text-slate-500">
                          of ${(campaign.target_amount || 0).toLocaleString()}
                        </span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-slate-500">{progress}% funded</span>
                        <span className="text-xs text-slate-500">{campaign.overall_progress || 0}% complete</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Link to={`/campaigns/${campaign.slug}`} className="flex-1">
                        <Button variant="outline" className="w-full" size="sm">
                          Learn More
                        </Button>
                      </Link>
                      <Link to={`/campaigns/${campaign.slug}/donate`}>
                        <Button className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                          <Heart className="h-4 w-4 mr-1" />
                          Donate
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsList;
